"use client"

import { useState, useEffect, useCallback } from "react"
import { SIDEBAR_KEYBOARD_KEYS, SIDEBAR_DATA_ATTRIBUTES } from "../Sidebar.constants"
import { useSidebarContext } from "./useSidebarContext"

/**
 * Valor de retorno del hook useSidebarKeyboard
 * @interface
 */
interface UseSidebarKeyboardReturn {
  /** Índice del item actualmente enfocado (-1 si ninguno) */
  focusedIndex: number
  /** Función para cambiar el índice enfocado manualmente */
  setFocusedIndex: (index: number) => void
  /** Callback para actualizar el indicador según el item enfocado */
  updateIndicatorForFocus: (index: number) => void
}

/**
 * Hook para gestionar la navegación por teclado en el Sidebar
 *
 * @description
 * Hook que implementa navegación accesible por teclado para los items
 * del sidebar, siguiendo las especificaciones WAI-ARIA:
 *
 * - ArrowDown: Navegar al siguiente item
 * - ArrowUp: Navegar al item anterior
 * - Home: Ir al primer item
 * - End: Ir al último item
 * - Tab: Navegación estándar que actualiza el indicador
 *
 * También sincroniza el indicador visual con el item enfocado.
 *
 * @returns {UseSidebarKeyboardReturn} Estado y funciones para navegación
 *
 * @example
 * ```tsx
 * function SidebarNav() {
 *   const { focusedIndex, updateIndicatorForFocus } = useSidebarKeyboard()
 *
 *   return (
 *     <nav>
 *       {items.map((item, index) => (
 *         <SidebarItem
 *           key={item.id}
 *           focused={index === focusedIndex}
 *           onFocus={() => updateIndicatorForFocus(index)}
 *         />
 *       ))}
 *     </nav>
 *   )
 * }
 * ```
 */
export function useSidebarKeyboard(): UseSidebarKeyboardReturn {
  const {
    contentRef,
    indicatorRef,
    navRef,
    items,
    setShowIndicatorForKeyboard,
    setIsOverItems,
    setCurrentItemElement,
  } = useSidebarContext()
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  /**
   * Actualiza la posición del indicador basándose en el item enfocado
   *
   * @param {number} index - Índice del item a enfocar
   * @param {HTMLElement[]} [itemsOverride] - Array de items frescos (opcional). Si se proporciona, se usa en lugar del array del context.
   */
  const updateIndicatorForFocus = useCallback(
    (index: number, itemsOverride?: HTMLElement[]) => {
      if (!indicatorRef.current || !navRef.current) return

      // Usar items pasadas como parámetro si existen, o del context como fallback
      const itemsToUse = itemsOverride || items

      if (index < 0 || index >= itemsToUse.length) return

      const item = itemsToUse[index]
      if (!item) return

      const navRect = navRef.current.getBoundingClientRect()
      const itemRect = item.getBoundingClientRect()

      // Calcular posición Y relativa al nav
      const y = itemRect.top - navRect.top + itemRect.height / 2

      // Actualizar posición del indicador
      indicatorRef.current.style.top = `${y}px`

      // Actualizar currentItemElement para sincronizar FluidIndicator
      setCurrentItemElement(item)

      // Mostrar indicador cuando se navega por teclado
      setShowIndicatorForKeyboard(true)
      // Activar isOverItems como en el monolito para que el indicador sea visible
      setIsOverItems(true)
    },
    [indicatorRef, navRef, items, setShowIndicatorForKeyboard, setIsOverItems, setCurrentItemElement],
  )

  /**
   * Manejador de eventos de teclado
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Solo procesar si hay un contentRef
      if (!contentRef.current) return

      // Obtener todos los items del sidebar
      const sidebarItems = Array.from(
        contentRef.current.querySelectorAll(`[${SIDEBAR_DATA_ATTRIBUTES.ITEM}]`),
      ) as HTMLElement[]

      if (sidebarItems.length === 0) return

      switch (e.key) {
        case SIDEBAR_KEYBOARD_KEYS.ARROW_DOWN: {
          e.preventDefault()
          setFocusedIndex((prev) => {
            const next = prev < sidebarItems.length - 1 ? prev + 1 : prev
            sidebarItems[next]?.focus()
            updateIndicatorForFocus(next, sidebarItems)
            return next
          })
          break
        }

        case SIDEBAR_KEYBOARD_KEYS.ARROW_UP: {
          e.preventDefault()
          setFocusedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : prev
            sidebarItems[next]?.focus()
            updateIndicatorForFocus(next, sidebarItems)
            return next
          })
          break
        }

        case SIDEBAR_KEYBOARD_KEYS.HOME: {
          e.preventDefault()
          sidebarItems[0]?.focus()
          setFocusedIndex(0)
          updateIndicatorForFocus(0, sidebarItems)
          break
        }

        case SIDEBAR_KEYBOARD_KEYS.END: {
          e.preventDefault()
          const lastIndex = sidebarItems.length - 1
          sidebarItems[lastIndex]?.focus()
          setFocusedIndex(lastIndex)
          updateIndicatorForFocus(lastIndex, sidebarItems)
          break
        }

        case SIDEBAR_KEYBOARD_KEYS.TAB: {
          // Actualizar indicador cuando se navega con Tab
          const activeElement = document.activeElement as HTMLElement
          const currentIndex = sidebarItems.indexOf(activeElement)

          if (currentIndex !== -1) {
            setFocusedIndex(currentIndex)
            updateIndicatorForFocus(currentIndex, sidebarItems)
          }
          break
        }

        case SIDEBAR_KEYBOARD_KEYS.ESCAPE: {
          // Quitar focus del sidebar
          ;(document.activeElement as HTMLElement)?.blur()
          setFocusedIndex(-1)
          setShowIndicatorForKeyboard(false)
          setIsOverItems(false)
          setCurrentItemElement(null)
          break
        }
      }
    }

    // Agregar listener global
    document.addEventListener("keydown", handleKeyDown)

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [contentRef, updateIndicatorForFocus, setIsOverItems])

  return {
    focusedIndex,
    setFocusedIndex,
    updateIndicatorForFocus,
  }
}
